import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'

import type { PartnerNames } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useTelcoPicker/types'

/**
 * Resolves which telco partner offer (if any) should be posted to the cart.
 *
 * TRUST BOUNDARY: a telco plan is only posted when the resolved offer exists in
 * the backend-provided `partnerPromoCodes` AND the backend has flagged it as
 * assignable (`assignment === true`).
 *
 * Two modes, selected by `telcoSelection.isPickerActive`:
 *
 * PICKER MODE (isPickerActive === true):
 *   The buyer's explicit `selectedPartnerName` is authoritative.
 *   - non-null: find the backend row by partnerName (unique closed enum),
 *     post iff `assignment === true`.
 *   - null (None): no telco service posted.
 *   The picker mode disables the legacy referral auto-assign below.
 *
 * LEGACY / SINGLE-OFFER / REFERRAL MODE (isPickerActive falsy):
 *   Auto-assign `partnerPromoCodes[0]` iff `assignment === true`.
 *   This preserves flag-off, referral, single-offer, and non-US behaviour.
 *
 * The returned `offerId` falls back to `id` (`offerId ?? id`) because the
 * service path needs the service offer id, which the backend exposes as
 * `offerId` when present and otherwise as `id`.
 */
export function getResolvedTelcoOffer({
  partnerPromoCodes,
  telcoSelection,
}: {
  partnerPromoCodes: GetProductResponse['includedServiceOffers']['partnerPromoCodes']
  telcoSelection?: {
    isPickerActive: boolean
    selectedPartnerName: PartnerNames | null
  }
}): {
  offerId: string
  partnerName: PartnerNames
} | null {
  if (telcoSelection?.isPickerActive) {
    // Picker path: selection is authoritative; referral auto-assign disabled.
    if (telcoSelection.selectedPartnerName) {
      const matched = partnerPromoCodes.find(
        (offer) => offer.partnerName === telcoSelection.selectedPartnerName,
      )

      if (matched && matched.assignment === true) {
        return {
          offerId: matched.offerId ?? matched.id,
          partnerName: matched.partnerName,
        }
      }
    }

    // None selected, or partner not assignable → no partner
    return null
  }

  // Legacy / single-offer / referral: auto-assign first offer iff assignable.
  const offer = partnerPromoCodes[0]

  if (offer && offer.assignment === true) {
    return {
      offerId: offer.offerId ?? offer.id,
      partnerName: offer.partnerName,
    }
  }

  return null
}
